import { createElement } from 'react';
import { captureException } from '@sentry/react';
import { BiDuplicate } from 'react-icons/bi';
import { TbArchive, TbArchiveOff, TbLock, TbLockOpen, TbStarFilled, TbStarOff } from 'react-icons/tb';
import { StateCreator } from 'zustand';

import { listViewService } from '@/services';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { getWorkspaceResourceTypeLabel } from '@/shared/components/workspace-resources';
import {
    appendEntity,
    createSingleFlightKey,
    mergeEntity,
    mergeEntityById,
    removeEntityById,
    replaceEntityById,
    replaceOptimisticEntityById,
    runSingleFlight,
    upsertEntityById,
} from '@/stores/helpers/collections';
import { reduceEntityDelta } from '@/stores/helpers/delta';
import { ListView } from '@/types';

import { ListViewActions, ListViewState } from '../types';

export const createListViewActions: StateCreator<
    ListViewState & ListViewActions,
    [['zustand/immer', never]],
    [],
    ListViewActions
> = (set, get, store) => ({
    // Core
    fetchListViews: async (params) => {
        set({ loading: true });

        try {
            const listViews = await listViewService.list(params);

            set({ listViews, loading: false, error: undefined });
        } catch (error) {
            captureException(error);

            set({ loading: false, error: 'Failed to load listViews' });
        }
    },
    fetchListViewById: async (id) => {
        set((state) => {
            state.loadingListView = true;
            state.error = undefined;
        });

        try {
            const listView = await listViewService.getById(id);

            set({ selectedListView: listView, loadingListView: false });
        } catch (error) {
            captureException(error);

            set((state) => {
                state.error = 'Failed to fetch listView detail';
                state.loadingListView = false;
            });
        }
    },
    clearSelectedListView: () => {
        set((state) => {
            state.selectedListView = null;
        });
    },
    createListView: async (input) =>
        runSingleFlight(createSingleFlightKey('listViews:create', input), async () => {
            const tempId = `temp-${Date.now()}`;
            const optimistic = { id: tempId, ...input } as ListView;

            set({ listViews: appendEntity(get().listViews, optimistic) });

            try {
                const listView = await listViewService.create(input);

                set({ listViews: replaceOptimisticEntityById(get().listViews, tempId, listView) });

                return listView;
            } catch (error) {
                captureException(error);

                set({ listViews: removeEntityById(get().listViews, tempId), error: 'Failed to create listView' });
            }
        }),
    updateListView: async (id, updates) => {
        const prev = get().listViews.find((f) => f.id === id);
        if (!prev) {
            return;
        }

        set({
            listViews: mergeEntityById(get().listViews, id, updates),
            selectedListView:
                get().selectedListView?.id === id
                    ? mergeEntity(get().selectedListView!, updates)
                    : get().selectedListView,
        });

        try {
            const listView = await listViewService.update(id, updates);

            set({
                listViews: mergeEntityById(get().listViews, id, listView),
                selectedListView:
                    get().selectedListView?.id === id
                        ? mergeEntity(get().selectedListView!, listView)
                        : get().selectedListView,
            });
        } catch (error) {
            captureException(error);

            set({
                listViews: replaceEntityById(get().listViews, id, prev),
                selectedListView: get().selectedListView?.id === id ? prev : get().selectedListView,
                error: 'Failed to update listView',
            });
        }
    },
    deleteListView: async (id) => {
        const prev = get().listViews;

        set({
            listViews: removeEntityById(get().listViews, id),
            selectedListView: get().selectedListView?.id === id ? null : get().selectedListView,
        });

        try {
            await listViewService.delete(id);
        } catch (error) {
            captureException(error);

            set({ listViews: prev, error: 'Failed to delete listView' });
        }
    },
    applyListViewDelta: (packet) => {
        if (packet.entity !== 'listView') {
            return;
        }

        const next = reduceEntityDelta<ListView>(get().listViews, get().selectedListView, packet);

        if (!next) {
            return;
        }

        set({
            listViews: next.items,
            selectedListView: next.selected,
        });
    },
    // - Aliases
    duplicateListView: async (id) => {
        const prev = get().listViews;
        const resourceTypeLabel = getWorkspaceResourceTypeLabel('list-view');

        if (!prev) {
            return;
        }

        try {
            const duplicated = await listViewService.duplicate(id);

            set({ listViews: upsertEntityById(get().listViews, duplicated, { prepend: true }) });

            toast(`${resourceTypeLabel} Duplicated`, {
                icon: createElement(BiDuplicate, { size: '1rem' }),
                description: duplicated.name,
                resource: { id: duplicated.id, parentId: duplicated.list?.id, type: 'list-view' },
            });

            return duplicated;
        } catch (error) {
            captureException(error);

            set({ listViews: prev, error: 'Failed to duplicate listView' });

            toast.error(`Unable to Duplicate ${resourceTypeLabel}`);
        }
    },
    toggleListViewFavorite: async (id, favorited) => {
        const prev = get().listViews.find((f) => f.id === id);

        if (!prev) {
            return;
        }

        set((state) => {
            const listView = state.listViews.find((f) => f.id === id);
            if (listView) {
                listView.favorited = favorited;
            }

            // Also update selectedListView if it matches
            if (state.selectedListView?.id === id) {
                state.selectedListView.favorited = favorited;
            }
        });

        try {
            await listViewService.setFavorite(id, favorited);

            toast(`${favorited ? 'Added to' : 'Removed from'} Favorites`, {
                icon: createElement(favorited ? TbStarFilled : TbStarOff, { size: '1rem' }),
                description: prev.name,
                resource: { id, parentId: prev.list?.id, type: 'list-view' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const listView = state.listViews.find((f) => f.id === id);
                if (listView) {
                    listView.favorited = prev.favorited;
                }

                // Revert selectedListView favorited if it matches
                if (state.selectedListView?.id === id) {
                    state.selectedListView.favorited = prev.favorited;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
    toggleListViewLock: async (id, locked) => {
        const prev = get().listViews.find((f) => f.id === id);
        const resourceTypeLabel = getWorkspaceResourceTypeLabel('list-view');

        if (!prev) {
            return;
        }

        set((state) => {
            const listView = state.listViews.find((f) => f.id === id);
            if (listView) {
                listView.locked = locked;
            }

            // Also update selectedListView if it matches
            if (state.selectedListView?.id === id) {
                state.selectedListView.locked = locked;
            }
        });

        try {
            await listViewService.setLocked(id, locked);

            toast(`${resourceTypeLabel} ${locked ? 'Locked' : 'Unlocked'}`, {
                icon: createElement(locked ? TbLock : TbLockOpen, { size: '1rem' }),
                description: prev.name,
                resource: { id, parentId: prev.list?.id, type: 'list-view' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const listView = state.listViews.find((f) => f.id === id);
                if (listView) {
                    listView.locked = prev.locked;
                }

                // Revert selectedListView locked if it matches
                if (state.selectedListView?.id === id) {
                    state.selectedListView.locked = prev.locked;
                }

                state.error = 'Failed to update lock';
            });
        }
    },
    toggleListViewArchive: async (id, archived) => {
        const prev = get().listViews.find((f) => f.id === id);
        const resourceTypeLabel = getWorkspaceResourceTypeLabel('list-view');

        if (!prev) {
            return;
        }

        set((state) => {
            const listView = state.listViews.find((f) => f.id === id);
            if (listView) {
                listView.archived = archived;
            }

            // Also update selectedListView if it matches
            if (state.selectedListView?.id === id) {
                state.selectedListView.archived = archived;
            }
        });

        try {
            await listViewService.setArchived(id, archived);

            toast(`${resourceTypeLabel} ${archived ? 'Archived' : 'Unarchived'}`, {
                icon: createElement(archived ? TbArchive : TbArchiveOff, { size: '1rem' }),
                description: prev.name,
                resource: { id, parentId: prev.list?.id, type: 'list-view' },
            });
        } catch (error) {
            captureException(error);

            set((state) => {
                const listView = state.listViews.find((f) => f.id === id);
                if (listView) {
                    listView.archived = prev.archived;
                }

                // Revert selectedListView archived if it matches
                if (state.selectedListView?.id === id) {
                    state.selectedListView.archived = prev.archived;
                }

                state.error = 'Failed to update favorite';
            });
        }
    },
    // Helper
    reorderListViews: (ordered: ListView[]) =>
        set((state) => ({
            listViews: ordered,
        })),
});
